// Classic script — NOT an ES module. Cannot import from @theme/*.
// `<quick-add-modal>` extends `<modal-dialog>` which now lives in
// secondary.js as an ES module (post-Tier-4 migration), so the bare
// `ModalDialog` identifier is no longer on globalThis. Look it up via
// the custom-elements registry instead. Defer ordering guarantees
// secondary.js has executed (and registered modal-dialog) before this
// script runs, since secondary.js appears earlier in theme.liquid.
if (!customElements.get('quick-add-modal')) {
  const ModalDialogClass = customElements.get('modal-dialog');
  if (ModalDialogClass) {
    class QuickAddModal extends ModalDialogClass {
      // Cache parsed product-page innerHTML keyed by `data-product-url`.
      // Static field — shared across all <quick-add-modal> instances on
      // the page (each product card has one). Opening the same product
      // twice on a collection page is instant after the first fetch.
      //
      // Cleared on `cart:update` — the rendered section can include
      // cart-aware fragments (already-in-cart messaging, gift-with-purchase
      // state, etc.) that would otherwise go stale.
      static #cache = new Map();
      static #cartUpdateBound = false;

      #abortController = null;

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        if (!QuickAddModal.#cartUpdateBound) {
          QuickAddModal.#cartUpdateBound = true;
          // Event name matches `ThemeEvents.cartUpdate` in main.js — kept
          // as a string literal here because classic scripts can't import
          // the constant.
          document.addEventListener('cart:update', () => QuickAddModal.#cache.clear());
        }
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        const productUrl = opener.getAttribute('data-product-url');
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const finish = () => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        };

        const cachedHtml = QuickAddModal.#cache.get(productUrl);
        if (cachedHtml) {
          this.setInnerHTML(this.modalContent, cachedHtml);
          this.#postRender(opener);
          finish();
          return;
        }

        // Cancel any earlier in-flight fetch — e.g. the user clicks quick-add
        // on card A, then immediately card B before A's fetch returns.
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;

        fetch(productUrl, { signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (signal.aborted) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            if (this.productElement.querySelector('media-gallery')) {
              this.productElement.querySelector('media-gallery').dataset.disablePrepend = 'false';
            }
            const html = this.productElement.innerHTML;
            QuickAddModal.#cache.set(productUrl, html);
            this.setInnerHTML(this.modalContent, html);
            this.#postRender(opener);
          })
          .catch((err) => {
            if (err?.name === 'AbortError') return;
            throw err;
          })
          .finally(finish);
      }

      // Work shared between cache-hit and cache-miss paths. Runs after
      // the modal's innerHTML has been set.
      #postRender(opener) {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.removeGalleryListSemantic();
        this.updateImageSizes();
        this.preventVariantURLSwitching();
        super.show(opener);
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach(oldScriptTag => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach(attribute => {
            newScriptTag.setAttribute(attribute.name, attribute.value)
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const stickyAtc = this.productElement.querySelector('sticky-atc');
        if (stickyAtc) stickyAtc.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach(modal => modal.remove());

        // Size chart dialog of the product page (its trigger is hidden in the quick add): its ids
        // (clothing-table, SizingChartTitle-…) would repeat the page's own.
        this.productElement.querySelectorAll('modal-v2').forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
      }
    }

    customElements.define('quick-add-modal', QuickAddModal);
  }
}
